import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  createRef
} from 'react';
import {
  useTable,
  useFlexLayout,
  useFilters,
  useSortBy,
  Row,
  useRowSelect,
  Cell,
  useRowState
} from 'react-table';
import InfiniteLoader from 'react-window-infinite-loader';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import update from 'immutability-helper';
import { map, find, equals, findIndex, reduce, pipe, filter, always } from 'ramda';
import styles from './styles.module.scss';
import { IBindingCallback1 } from '../../common/models/callback';
import HeaderCell from './HeaderCell';
import LoaderWrapper from '../LoaderWrapper';
import { ISortItem } from '../../common/models/filter/ISortItem';
import { SortType } from '../../common/enums/SortType';
import NoDataPlaceholder from '../NoDataPlaceholder';
import { usePrevious } from '../../common/helpers/customHooks';
import { ITableConfig } from '../../common/models/table/ITableConfig';
import { ITableRef } from '../../common/models/table/ITableRef';
import TableRow from './TableRow';
import { IEditableRow } from '../../common/models/table/IEditableRow';
import { dragColumn, getCheckboxColumn } from './utils';
import { IRowItemData } from '../../common/models/table/IRowItemData';
import { concat } from '../../common/helpers/arrayHelper';

interface IProps {
  className?: string;
  config: ITableConfig;
}

const Table = forwardRef<ITableRef, IProps>((props, pref) => {
  const {
    className = '',
    config: {
      columns,
      data: {
        items: data = [],
        total = 0
      },

      loading = false,
      pinnedRow,
      noDataPlaceholder = <NoDataPlaceholder />,

      rowSelection,
      selectionType = 'checkbox',
      hideCheckboxHeader = false,

      draggable = false,

      hideHeader = false,
      sortingEnabled = false,
      // filteringEnabled = false, disabled column filtering for now

      defaultSelected = [],
      defaultSorted = [],

      focusable = false,
      defaultFocused,

      autoHeight = false,
      rowHeight = 55,
      rowIndent = 10,
      noDataPlaceholderHeight = 15,

      footer,
      footerHeight = 55,

      onLoad = () => null,
      onFilterUpdated = () => null,
      onRowClicked = () => null,
      onRowMouseDown = () => null,
      onRowSelected = () => null,
      onSelectionChanged = () => null,
      onCellClicked = () => null,
      itemKey,
      isRowDraggable,
      onDragDrop = () => null
    }
  } = props;

  const [items, setItems] = useState(data);
  const [preSelectAll, setPreSelectAll] = useState(false);
  const [dataByScroll, setDataByScroll] = useState(true);
  const [focusedRow, setFocusedRow] = useState<Row>();
  const [editableRow, setEditableRow] = useState<IEditableRow>();

  let listRef: List;
  const tableRef = useRef<HTMLDivElement>(null);
  const infiniteLoaderRef = useRef<InfiniteLoader>(null);
  const outerListRef = useRef<Element>(null);
  const tableHeaderRef = createRef<HTMLDivElement>();

  const actualRowHeight = rowHeight + rowIndent;
  const actualHeaderHeight = 35; // see scss
  const actualFooterHeight = footer ? footerHeight : 0;
  const actualPinnedRowHeight = pinnedRow ? rowHeight : 0;
  const actualNoDataPlaceholderHeight = !items.length ? noDataPlaceholderHeight : 0;

  const withCheckboxClass = rowSelection && selectionType !== 'row' && styles.withCheckbox;

  const loadItems = (from: number, to: number) => {
    onLoad(from, to);
    setDataByScroll(true);
    return null; // InfiniteLoader requires return
  };

  const editRow = (row: Row) => {
    const editData = pipe(
      filter<any, 'array'>(cell => cell.editable !== false),
      reduce((acc, cell) => ({ ...acc, [cell.column.id]: cell.value }), {})
    )(row.cells);

    setEditableRow({
      index: row.index,
      values: editData
    });
  };

  const updateRowEditData = (columnId: string, value: any) => {
    if (editableRow) {
      editableRow.values[columnId] = value;
      setEditableRow(editableRow);
    }
  };

  const getRowEditData = () => editableRow;

  const cancelRowEdit = () => setEditableRow(undefined);

  const selectedRowIds = reduce(
    (acc, item) => ({ ...acc, [findIndex(row => equals(row, item), items)]: true }),
    {},
    defaultSelected
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy, filters },
    setAllFilters,
    toggleRowSelected,
    toggleAllRowsSelected,
    selectedFlatRows,
    columns: columnsInstances
  } = useTable({
    columns,
    data: items,
    manualSortBy: true,
    manualFilters: true,
    disableMultiSort: true,
    defaultCanSort: true,
    defaultCanFilter: false, // disabled column filtering for now
    autoResetSortBy: false,
    autoResetFilters: false,
    autoResetSelectedRows: true,
    sortTypes: SortType as any,
    initialState: {
      selectedRowIds,
      sortBy: defaultSorted
    },
    editRow,
    getRowEditData,
    cancelRowEdit
  },
  useFlexLayout,
  useFilters,
  useSortBy,
  useRowSelect,
  useRowState,
  hooks => {
    if (rowSelection && selectionType !== 'row') {
      const checkboxColumn = getCheckboxColumn(
        hideCheckboxHeader,
        rowSelection,
        total,
        items.length,
        onLoad,
        setPreSelectAll,
        selectionType === 'checkbox' ? onRowSelected : always(undefined)
      );
      hooks.visibleColumns.push(cols => [checkboxColumn, ...cols]);
    }
    if (draggable) {
      hooks.visibleColumns.push(cols => [dragColumn, ...cols]);
    }
  });

  const prevSelected = usePrevious(selectedFlatRows);

  const onCellClick = (cell: Cell, event: React.MouseEvent<HTMLElement>) => {
    const accessor = cell.column.id;
    onCellClicked({ accessor, original: cell.row.original }, event);
  };

  const onRowClick = (row: Row, enterPressed = false) => {
    if (focusable) {
      setFocusedRow(row);
    }

    onRowClicked(row.original);

    const canNotDeselect = rowSelection === 'single' && row.isSelected;
    if (!rowSelection || canNotDeselect || (selectionType === 'checkbox' && !enterPressed)) {
      return;
    }

    if (rowSelection === 'single') {
      toggleAllRowsSelected(false);
    }

    toggleRowSelected(row.id);
    onRowSelected(row.original, !row.isSelected);
  };

  const keyHandler = (event: KeyboardEvent) => {
    if (!focusable) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        const index = rows.findIndex(row => row.id === focusedRow?.id);
        const next = event.key === 'ArrowUp' ? index - 1 : index + 1;
        if (rows[next]) {
          event.preventDefault();
          listRef.scrollToItem(next, 'smart');
          setFocusedRow(rows[next]);
        }
        break;
      case 'Enter':
        if (focusedRow && !(focusedRow.original as any).disabled) {
          onRowClick(focusedRow, true);
        }
      // no default
    }
  };

  const onMouseMove = (row: Row) => {
    if (focusable) {
      setFocusedRow(row);
    }
  };

  const syncHeaderScroll = (event: Event) => {
    if (tableHeaderRef.current) {
      const element = event.target as HTMLDivElement;
      tableHeaderRef.current.scrollTo({ left: element.scrollLeft }); // sync header and body scrolls
    }
  };

  const resetSort = () => {
    const sortedColumnsIds = sortBy.map(x => x.id);
    columnsInstances.forEach(x => {
      if (sortedColumnsIds.includes(x.id)) x.clearSortBy();
    });
  };

  const moveRow = (dragIndex: number, hoverIndex: number) => {
    setItems(old => update(old, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, items[dragIndex]]
      ]
    }));
  };

  const getItemKey = (index: number, itemData: any) => {
    const row = itemData.rows[index];
    if (row && itemKey) {
      return itemKey(row.original);
    }
    return (row && row.original.id) || index;
  };

  const itemData: IRowItemData = {
    editableRowIndex: editableRow?.index,
    focusedRowId: focusedRow?.id,
    rows,
    rowHeight,
    prepareRow,
    moveRow,
    updateRowEditData,
    onCellClick,
    onRowClick,
    onRowMouseDown,
    onMouseMove,
    isRowDraggable,
    onDragDrop: () => onDragDrop(items)
  };

  const { style: tableStyle = {}, ...restTableProps } = getTableProps();
  if (autoHeight) {
    tableStyle.height = actualHeaderHeight
      + actualPinnedRowHeight + items.length * actualRowHeight + actualFooterHeight + actualNoDataPlaceholderHeight
      + rowIndent; // add row indent to avoid showing scroll due to it's auto
  }

  useImperativeHandle(pref, () => ({
    clearFilters() {
      resetSort();
      setAllFilters([]);
    },
    deselectRows() {
      toggleAllRowsSelected(false);
    }
  }));

  useEffect(() => {
    setItems(data);
  }, [data]);

  useEffect(() => {
    if (preSelectAll) {
      toggleAllRowsSelected(true);
      setPreSelectAll(false);
    }
  }, [items]);

  useEffect(() => {
    onFilterUpdated({ sort: sortBy as ISortItem[], filter: filters });
  }, [sortBy, filters]);

  useEffect(() => {
    const selected = map(row => row.original, selectedFlatRows);
    if (selected.length !== prevSelected?.length) {
      onSelectionChanged(selected);
    }
  }, [selectedFlatRows]);

  useEffect(() => {
    const row = find(x => equals(x.original, defaultFocused), rows);
    setFocusedRow(row);
  }, [defaultFocused, rows]);

  useEffect(() => {
    window.addEventListener('keydown', keyHandler);
    return () => {
      window.removeEventListener('keydown', keyHandler);
    };
  }, [keyHandler]);

  useEffect(() => {
    if (listRef && !dataByScroll) {
      listRef.scrollToItem(0, 'start');
      if (infiniteLoaderRef.current) {
        infiniteLoaderRef.current.resetloadMoreItemsCache();
      }
      setEditableRow(undefined);
    }
    setDataByScroll(false);
  }, [items]);

  useEffect(() => {
    if (outerListRef.current) {
      outerListRef.current.addEventListener('scroll', syncHeaderScroll);
    }
    return () => {
      if (outerListRef.current) {
        outerListRef.current.removeEventListener('scroll', syncHeaderScroll);
      }
    };
  }, [outerListRef, tableHeaderRef]);

  return (
    <LoaderWrapper loading={loading || false} transparent>
      <div
        {...restTableProps}
        className={concat(styles.table, className, withCheckboxClass)}
        ref={tableRef}
        style={tableStyle}
      >
        {!hideHeader && (
          <div className={`hr-table-head ${styles.thead}`} ref={tableHeaderRef}>
            {headerGroups.map(headerGroup => (
              <div className={styles.theadInner} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <HeaderCell
                    className={styles.th}
                    sortingEnabled={Boolean(sortingEnabled && column.canSort)}
                    // filteringEnabled={Boolean(filteringEnabled && column.canFilter)} // disabled column filtering
                    filteringEnabled={false}
                    key={column.id}
                    column={column}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
        {!loading && !total
          ? noDataPlaceholder
          : (
            <div {...getTableBodyProps()} className={`hr-table-body ${styles.tbody}`}>
              {pinnedRow && <div style={{ height: rowHeight }}>{pinnedRow}</div>}
              <AutoSizer>
                {({ height, width }) => (
                  <InfiniteLoader
                    isItemLoaded={(index: number) => Boolean(items[index])}
                    itemCount={total}
                    loadMoreItems={loadItems}
                    ref={infiniteLoaderRef}
                  >
                    {({ onItemsRendered, ref: setListRef }) => (
                      <List
                        height={height - actualPinnedRowHeight - actualFooterHeight}
                        itemCount={total}
                        outerRef={outerListRef}
                        ref={(ref: List) => {
                          listRef = ref;
                          (setListRef as IBindingCallback1<List>)(ref);
                        }}
                        onItemsRendered={onItemsRendered}
                        itemSize={rowHeight + rowIndent}
                        width={width}
                        itemKey={getItemKey}
                        itemData={itemData}
                      >
                        {TableRow}
                      </List>
                    )}
                  </InfiniteLoader>
                )}
              </AutoSizer>
              {footer && (
                <div className={styles.footer} style={{ height: footerHeight }}>
                  {footer}
                </div>
              )}
            </div>
          )}
      </div>
    </LoaderWrapper>
  );
});

export default Table;
